import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {updateTransform} from "@template-editor/helpers/three-object"
import {SphereLight} from "@template-editor/helpers/three-point-light"
import {SunLight} from "@template-editor/helpers/three-sun-light"

export interface ShadowLightProxy {
    sync(): void
    addToScene(scene: THREE.Scene): void
    jitter(offsetX: number, offsetY: number): void
    configureForScene?(sceneBoundingSphere: THREE.Sphere | null): void
}

const SHADOW_MAP_SIZE = 1024

const SHADOW_FILTER_OFFSETS: [number, number][] = [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
]

const SHADOW_DEPTH_EPSILON = 1e-5

const snapToTexelCenter = (coordinate: THREE.Node<"vec2">) => coordinate.mul(SHADOW_MAP_SIZE).floor().add(0.5).div(SHADOW_MAP_SIZE)

// Every tap compares against the receiver's own plane, evaluated at the center of the texel the tap reads (adaptive receiver
// plane depth bias). Without it, the conservative minimum over neighboring texels lets a sloped surface shadow itself, and
// a thin shell its own back face.
const receiverPlaneDirectionalShadowFilter = ({depthTexture, shadowCoord}: {depthTexture: THREE.DepthTexture; shadowCoord: THREE.Node<"vec3">}) => {
    const dCoorddx = TSL.dFdx(shadowCoord)
    const dCoorddy = TSL.dFdy(shadowCoord)
    const dUVdx = dCoorddx.xy
    const dUVdy = dCoorddy.xy
    const dZdx = dCoorddx.z
    const dZdy = dCoorddy.z
    const det = dUVdx.x.mul(dUVdy.y).sub(dUVdx.y.mul(dUVdy.x))
    const hasPlane = det.abs().greaterThan(1e-24)
    const safeDet = hasPlane.select(det, TSL.float(1))
    const depthGradient = hasPlane.select(
        TSL.vec2(dZdx.mul(dUVdy.y).sub(dUVdx.y.mul(dZdy)), dUVdx.x.mul(dZdy).sub(dZdx.mul(dUVdy.x))).div(safeDet),
        TSL.vec2(0, 0),
    )

    const compareAt = (offset: [number, number]) => {
        const texelCenter = snapToTexelCenter(shadowCoord.xy.add(TSL.vec2(offset[0] / SHADOW_MAP_SIZE, offset[1] / SHADOW_MAP_SIZE)))
        const planeDepth = shadowCoord.z.add(depthGradient.dot(texelCenter.sub(shadowCoord.xy))).sub(SHADOW_DEPTH_EPSILON)
        return TSL.texture(depthTexture, texelCenter).compare(planeDepth) as THREE.Node<"float">
    }

    const result = compareAt([0, 0]).toVar()
    for (const offset of SHADOW_FILTER_OFFSETS) result.assign(result.min(compareAt(offset)))
    return result
}

// Returns a direction whose major component is ±1 and whose other two point at the center of the cube face texel it hits
const snapToCubeTexelCenter = (direction: THREE.Node<"vec3">) => {
    const absDirection = direction.abs()
    const isX = absDirection.x.greaterThanEqual(absDirection.y).and(absDirection.x.greaterThanEqual(absDirection.z))
    const isY = isX.not().and(absDirection.y.greaterThanEqual(absDirection.z))
    const major = isX.select(absDirection.x, isY.select(absDirection.y, absDirection.z))
    const faceDirection = direction.div(major)
    const snapped = faceDirection
        .mul(0.5)
        .add(0.5)
        .mul(SHADOW_MAP_SIZE)
        .floor()
        .min(SHADOW_MAP_SIZE - 1)
        .add(0.5)
        .div(SHADOW_MAP_SIZE)
        .mul(2)
        .sub(1)
    return TSL.vec3(
        isX.select(faceDirection.x.sign(), snapped.x),
        isY.select(faceDirection.y.sign(), snapped.y),
        isX.or(isY).select(snapped.z, faceDirection.z.sign()),
    )
}

const receiverPlanePointShadowFilter = (light: THREE.PointLight) => {
    const near = TSL.uniform(0).onRenderUpdate(() => light.shadow.camera.near)
    const far = TSL.uniform(0).onRenderUpdate(() => light.shadow.camera.far)

    return ({depthTexture, bd3D, dp}: {depthTexture: THREE.CubeTexture; bd3D: THREE.Node<"vec3">; dp: THREE.Node<"float">}) => {
        const toFragment = TSL.positionWorld.sub(TSL.lightPosition(light))
        const planeNormal = TSL.cross(TSL.dFdx(toFragment), TSL.dFdy(toFragment))
        const planeDistance = planeNormal.dot(toFragment)

        const planeDepthAlong = (direction: THREE.Node<"vec3">) => {
            const denominator = planeNormal.dot(direction)
            const hits = denominator.abs().greaterThan(planeNormal.length().mul(1e-4))
            const viewZ = planeDistance.div(hits.select(denominator, TSL.float(1)))
            const planeDepth = TSL.viewZToPerspectiveDepth(viewZ.mul(1 - SHADOW_DEPTH_EPSILON).negate(), near, far)
            return hits.and(viewZ.greaterThan(0)).select(planeDepth, dp)
        }

        const absDir = bd3D.abs()
        const tangent = bd3D.cross(absDir.x.greaterThan(absDir.z).select(TSL.vec3(0, 1, 0), TSL.vec3(1, 0, 0))).normalize()
        const bitangent = bd3D.cross(tangent)
        // One cube face texel spans 2/SHADOW_MAP_SIZE in face coordinates, and a tangential offset e on the unit
        // direction moves the face coordinate by e/cos²θ (θ = angle to the dominant axis, cosθ = its |component|),
        // so a full-texel offset needs e = 2·cos²θ/SHADOW_MAP_SIZE
        const cosTheta = absDir.x.max(absDir.y).max(absDir.z)
        const texelOffset = cosTheta.mul(cosTheta).mul(2 / SHADOW_MAP_SIZE)

        const compareAt = (offset: [number, number]) => {
            const texelCenter = snapToCubeTexelCenter(bd3D.add(tangent.mul(texelOffset.mul(offset[0]))).add(bitangent.mul(texelOffset.mul(offset[1]))))
            return TSL.cubeTexture(depthTexture, texelCenter).compare(planeDepthAlong(texelCenter)) as THREE.Node<"float">
        }

        const result = compareAt([0, 0]).toVar()
        for (const offset of SHADOW_FILTER_OFFSETS) result.assign(result.min(compareAt(offset)))
        return result
    }
}

export const getSceneBoundingSphere = (lightObjects: readonly (THREE.Light | THREE.Mesh)[]) => {
    const box = new THREE.Box3()
    for (const object of lightObjects) if (object instanceof THREE.Mesh) box.expandByObject(object)
    return box.isEmpty() ? null : box.getBoundingSphere(new THREE.Sphere())
}

const configurePointShadowForScene = (
    shadow: THREE.PointLightShadow,
    sourceMatrixWorld: THREE.Matrix4,
    jitterReach: number,
    sceneBoundingSphere: THREE.Sphere | null,
) => {
    const center = sceneBoundingSphere ? sceneBoundingSphere.center : new THREE.Vector3()
    const radius = sceneBoundingSphere ? Math.max(sceneBoundingSphere.radius, 1) : 1000
    const lightPosition = new THREE.Vector3().setFromMatrixPosition(sourceMatrixWorld)
    // Depth precision is spent between near and far, so a light outside the scene starts its range at the scene. Cube face
    // depth is the largest component of the light-to-point vector, which can be as small as the distance / √3.
    shadow.camera.near = Math.max(radius * 1e-3, (lightPosition.distanceTo(center) - radius - jitterReach) / Math.sqrt(3))
    shadow.camera.far = lightPosition.distanceTo(center) + radius + jitterReach
    shadow.camera.updateProjectionMatrix()
}

class RectAreaLightProxy extends THREE.PointLight implements ShadowLightProxy {
    private sourceLight?: THREE.RectAreaLight

    constructor() {
        super()
        this.castShadow = true
        this.shadow.mapSize.width = SHADOW_MAP_SIZE
        this.shadow.mapSize.height = SHADOW_MAP_SIZE
        this.shadow.camera.near = 10
        this.shadow.camera.far = 2000
        // @ts-expect-error filterNode is not in the type definitions
        this.shadow.filterNode = receiverPlanePointShadowFilter(this)
    }

    init(sourceLight: THREE.RectAreaLight) {
        this.sourceLight = sourceLight
        return this
    }

    private get source() {
        if (!(this.sourceLight instanceof THREE.RectAreaLight)) throw new Error("RectAreaLightProxy is not initialized")
        return this.sourceLight
    }

    sync() {
        this.visible = this.source.visible
        this.color.set(this.source.color)
        this.intensity = this.source.intensity * this.source.width * this.source.height
    }

    addToScene(scene: THREE.Scene) {
        const lightGroup = new THREE.Group()
        updateTransform(this.source.matrixWorld, lightGroup)
        lightGroup.add(this)
        scene.add(lightGroup)
    }

    configureForScene(sceneBoundingSphere: THREE.Sphere | null) {
        configurePointShadowForScene(this.shadow, this.source.matrixWorld, Math.max(this.source.width, this.source.height), sceneBoundingSphere)
    }

    jitter(offsetX: number, offsetY: number) {
        this.position.x = offsetX * this.source.width
        this.position.y = offsetY * this.source.height
    }
}

class SphereLightShadowProxy extends THREE.PointLight implements ShadowLightProxy {
    private sourceLight?: SphereLight

    constructor() {
        super()
        this.castShadow = true
        this.shadow.mapSize.width = SHADOW_MAP_SIZE
        this.shadow.mapSize.height = SHADOW_MAP_SIZE
        this.shadow.camera.near = 10
        this.shadow.camera.far = 2000
        // @ts-expect-error filterNode is not in the type definitions
        this.shadow.filterNode = receiverPlanePointShadowFilter(this)
    }

    init(sourceLight: SphereLight) {
        this.sourceLight = sourceLight
        return this
    }

    private get source() {
        if (!(this.sourceLight instanceof SphereLight)) throw new Error("SphereLightShadowProxy is not initialized")
        return this.sourceLight
    }

    sync() {
        this.visible = this.source.visible
        this.color.set(this.source.color)
        this.intensity = this.source.intensity
    }

    addToScene(scene: THREE.Scene) {
        const lightGroup = new THREE.Group()
        updateTransform(this.source.matrixWorld, lightGroup)
        lightGroup.add(this)
        scene.add(lightGroup)
    }

    configureForScene(sceneBoundingSphere: THREE.Sphere | null) {
        configurePointShadowForScene(this.shadow, this.source.matrixWorld, this.source.radius, sceneBoundingSphere)
    }

    jitter(offsetX: number, offsetY: number) {
        // Map the 2D jitter onto a uniform point on the sphere surface (Archimedes area-preserving map)
        const z = Math.max(-1, Math.min(1, -2 * offsetX))
        const rxy = Math.sqrt(Math.max(0, 1 - z * z))
        const phi = 2 * Math.PI * (offsetY + 0.5)
        this.position.set(Math.cos(phi) * rxy * this.source.radius, Math.sin(phi) * rxy * this.source.radius, z * this.source.radius)
    }
}

class SunLightShadowProxy extends THREE.DirectionalLight implements ShadowLightProxy {
    private sceneCenter = new THREE.Vector3()
    private sceneDistance = 3000
    private sourceLight?: SunLight

    constructor() {
        super()
        this.castShadow = true
        this.shadow.mapSize.width = SHADOW_MAP_SIZE
        this.shadow.mapSize.height = SHADOW_MAP_SIZE
        // @ts-expect-error filterNode is not in the type definitions
        this.shadow.filterNode = receiverPlaneDirectionalShadowFilter
    }

    init(sourceLight: SunLight) {
        this.sourceLight = sourceLight
        return this
    }

    private get source() {
        if (!(this.sourceLight instanceof SunLight)) throw new Error("SunLightShadowProxy is not initialized")
        return this.sourceLight
    }

    sync() {
        this.visible = this.source.visible
        this.color.set(this.source.color)
        this.intensity = this.source.intensity
    }

    addToScene(scene: THREE.Scene) {
        scene.add(this)
        scene.add(this.target)
    }

    // A sun is directional: its orthographic shadow camera must cover the whole scene
    configureForScene(sceneBoundingSphere: THREE.Sphere | null) {
        const center = sceneBoundingSphere ? sceneBoundingSphere.center : new THREE.Vector3()
        const radius = sceneBoundingSphere ? Math.max(sceneBoundingSphere.radius, 1) : 1000
        const distance = radius * 3
        const cam = this.shadow.camera
        cam.left = -radius
        cam.right = radius
        cam.top = radius
        cam.bottom = -radius
        cam.near = Math.max(0.1, distance - radius)
        cam.far = distance + radius
        cam.updateProjectionMatrix()
        this.sceneCenter.copy(center)
        this.sceneDistance = distance
    }

    jitter(offsetX: number, offsetY: number) {
        // Base travel direction = the sun's local -z in world space, jittered within its angular radius
        // (±angle/2) for soft shadows. The proxy sits sunDistance behind the scene center along it.
        const dir = new THREE.Vector3(0, 0, -1).transformDirection(this.source.matrixWorld).normalize()
        const u = (Math.abs(dir.y) < 0.999 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)).cross(dir).normalize()
        const v = new THREE.Vector3().crossVectors(dir, u)
        const angleRad = (this.source.angle * Math.PI) / 180
        const jittered = dir
            .clone()
            .addScaledVector(u, Math.tan(offsetX * angleRad))
            .addScaledVector(v, Math.tan(offsetY * angleRad))
            .normalize()
        this.position.copy(this.sceneCenter).addScaledVector(jittered, -this.sceneDistance)
        this.target.position.copy(this.sceneCenter)
    }
}

export type ShadowTerm = {shadow: THREE.Node<"float">; weight: THREE.Node<"float">}

export interface ShadowContributor {
    shadowContribution(): ShadowTerm
}

export const isShadowContributor = (node: unknown): node is ShadowContributor => !!node && typeof (node as ShadowContributor).shadowContribution === "function"

const lightBrightness = (light: THREE.Light) =>
    TSL.uniform(0).onRenderUpdate(function () {
        const {color, intensity} = light
        this.value = ((color.r + color.g + color.b) / 3.0) * intensity
    })

const weightedShadow = (node: THREE.Node, weight: THREE.Node<"float">): ShadowTerm => {
    // @ts-expect-error shadowNode is not in the type definitions
    const shadow: THREE.Node<"float"> = node.shadowNode.mul(weight)
    return {shadow, weight}
}

abstract class PointShadowProxyNode extends THREE.PointLightNode implements ShadowContributor {
    constructor(light?: THREE.PointLight | null) {
        super(light)
    }

    abstract shadowContribution(): ShadowTerm

    protected pointTerm() {
        const light = this.light
        if (!light) return null

        const lightColor = lightBrightness(light)
        const decay = TSL.uniform(2.0).onRenderUpdate(function () {
            this.value = light.decay
        })

        const lightRay = TSL.sub(TSL.lightPosition(light), TSL.positionWorld)
        const lightDistance = lightRay.length()
        const lightDirection = lightRay.div(lightDistance)

        const nDotL = TSL.normalWorld.dot(lightDirection).max(0.0)
        const attenuation = lightDistance.pow(decay).max(0.01).reciprocal().mul(lightColor).max(0.0)

        return {weight: attenuation.mul(nDotL), lightDirection}
    }
}

class SphereLightShadowProxyNode extends PointShadowProxyNode {
    override shadowContribution(): ShadowTerm {
        const term = this.pointTerm()
        return weightedShadow(this, term ? term.weight : TSL.float(0))
    }
}

class RectAreaLightProxyNode extends PointShadowProxyNode {
    override shadowContribution(): ShadowTerm {
        const term = this.pointTerm()
        const light = this.light
        if (!term || !light) return weightedShadow(this, TSL.float(0))

        const lightOrientation = TSL.uniform(new THREE.Vector3()).onRenderUpdate(function (frame: THREE.NodeFrame) {
            const {camera} = frame
            if (camera) {
                this.value.copy(new THREE.Vector3(0, 0, 1).transformDirection(light.matrixWorld).transformDirection(camera.matrixWorldInverse))
            }
        })

        return weightedShadow(this, term.weight.mul(term.lightDirection.dot(lightOrientation).max(0.0)))
    }
}

class SunLightShadowProxyNode extends THREE.DirectionalLightNode implements ShadowContributor {
    constructor(light?: THREE.DirectionalLight | null) {
        super(light)
    }

    shadowContribution(): ShadowTerm {
        const light = this.light
        if (!light) return weightedShadow(this, TSL.float(0))

        const lightColor = lightBrightness(light)
        // World-space direction toward the light (surface -> light) = normalize(lightPos - targetPos); a sun is
        // directional, so there is no distance attenuation.
        const toLight = TSL.uniform(new THREE.Vector3()).onRenderUpdate(function () {
            this.value.copy(light.getWorldPosition(new THREE.Vector3())).sub(light.target.getWorldPosition(new THREE.Vector3())).normalize()
        })
        const nDotL = TSL.normalWorld.dot(toLight).max(0.0)

        return weightedShadow(this, lightColor.mul(nDotL))
    }
}

export const registerShadowLightProxies = (renderer: THREE.WebGPURenderer) => {
    const lib = renderer.library
    if (!lib.lightNodes.has(RectAreaLightProxy)) lib.addLight(RectAreaLightProxyNode, RectAreaLightProxy)
    if (!lib.lightNodes.has(SphereLightShadowProxy)) lib.addLight(SphereLightShadowProxyNode, SphereLightShadowProxy)
    if (!lib.lightNodes.has(SunLightShadowProxy)) lib.addLight(SunLightShadowProxyNode, SunLightShadowProxy)
}

export const createShadowLightProxy = (light: THREE.Light): (THREE.Light & ShadowLightProxy) | null => {
    if (light instanceof THREE.RectAreaLight) return new RectAreaLightProxy().init(light)
    if (light instanceof SphereLight) return new SphereLightShadowProxy().init(light)
    if (light instanceof SunLight) return new SunLightShadowProxy().init(light)
    return null
}
